import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft, Clock, CheckCircle2,
  Send, ExternalLink, Video, Target, Sparkles, FileText,
  MessageCircle, Pencil, PartyPopper, Lock, Trash2
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Avatar } from '../components/ui/Avatar';
import { EmptyState } from '../components/ui/EmptyState';
import { useSupabaseClient } from '../hooks/useSupabaseClient';
import { useToast } from '../components/Toast';
import { getAssignmentUrl, formatTurkishDueDate } from '../utils/assignment';
import { formatStudentName } from '../utils/student';
import { EmoteText } from '../components/EmoteText';
import { EmojiPickerButton } from '../components/EmojiPickerButton';
import { ReactionBar } from '../components/ReactionBar';
import type { AssignmentRow, ClassRow } from '../types/sinif';

interface CommentItem {
  id: string;
  user_id: string;
  user_name: string;
  avatar_url?: string;
  content: string;
  created_at: string;
}

interface StudentSubmissionStatus {
  student_id: string;
  display_name: string;
  status: 'atandi' | 'devam_ediyor' | 'tamamlandi';
  completed_at?: string | null;
}

export default function AssignmentDetail() {
  const { assignmentId } = useParams<{ classId: string; assignmentId: string }>();
  const { user } = useUser();
  const navigate = useNavigate();
  const getSupabase = useSupabaseClient();
  const { showToast } = useToast();

  const [assignment, setAssignment] = useState<AssignmentRow | null>(null);


  const [classRow, setClassRow] = useState<ClassRow | null>(null);
  const [teacherName, setTeacherName] = useState<string>('Öğretmen');
  const [studentStatuses, setStudentStatuses] = useState<StudentSubmissionStatus[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Ödev Yönergesi Düzenleme State (Öğretmen için)
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [descText, setDescText] = useState('');
  const [isSavingDesc, setIsSavingDesc] = useState(false);

  // Yüklenen dosya state (Öğrenci teslimi için)
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Yorumlar state
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [newComment, setNewComment] = useState('');
  const newCommentInputRef = useRef<HTMLInputElement>(null);

  const loadData = useCallback(async () => {
    if (!assignmentId) return;
    const supabase = await getSupabase();

    // 1. Ödev detayını çek
    const { data: aData } = await supabase.from('assignments').select('*').eq('id', assignmentId).single();
    if (aData) {
      const a = aData as AssignmentRow;
      setAssignment(a);
      setDescText(a.description || '');
      setIsSubmitted(a.status === 'tamamlandi');

      // Ödevi oluşturan öğretmenin ismini çek
      if (a.teacher_id) {
        const { data: tProf } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('user_id', a.teacher_id)
          .maybeSingle();

        const name = tProf?.full_name || (a.teacher_id === user?.id ? user.fullName || user.firstName : null);
        if (name) setTeacherName(name);
      }

      // 2. Sınıf bilgisini çek
      if (a.class_id) {
        const { data: cData } = await supabase.from('classes').select('*').eq('id', a.class_id).single();
        if (cData) setClassRow(cData as ClassRow);
      }

      // 3. Öğretmen bakış açısı: bu sınıftaki tüm öğrencilerin teslim durumlarını çek
      if (a.class_id && user?.id === a.teacher_id) {
        const { data: allAssignments } = await supabase
          .from('assignments')
          .select('student_id, status, completed_at')
          .eq('class_id', a.class_id)
          .eq('title', a.title);

        if (allAssignments && allAssignments.length > 0) {
          const studentIds = allAssignments.map((row: any) => row.student_id);
          const [{ data: snaps }, { data: profs }] = await Promise.all([
            supabase.from('student_snapshots').select('student_id, display_name').in('student_id', studentIds),
            supabase.from('profiles').select('user_id, full_name').in('user_id', studentIds),
          ]);

          const snapMap = new Map((snaps ?? []).map((s: any) => [s.student_id, s.display_name]));
          const profMap = new Map((profs ?? []).map((p: any) => [p.user_id, p.full_name]));

          const list: StudentSubmissionStatus[] = allAssignments.map((row: any) => {
            const sName = snapMap.get(row.student_id);
            const pName = profMap.get(row.student_id);
            const raw = sName && !sName.startsWith('user_') ? sName : pName ?? sName;
            return {
              student_id: row.student_id,
              display_name: formatStudentName(raw, 'Öğrenci'),
              status: row.status,
              completed_at: row.completed_at,
            };
          });

          setStudentStatuses(list);
        }
      }

      // 4. Ödev yorumlarını Supabase class_comments tablosundan çek
      const { data: cList } = await supabase
        .from('class_comments')
        .select('*')
        .eq('assignment_id', assignmentId)
        .order('created_at', { ascending: true });

      if (cList) {
        setComments(
          cList.map(c => ({
            id: c.id,
            user_id: c.author_id,
            user_name: c.author_name,
            content: c.content,
            created_at: c.created_at,
          }))
        );
      }
    }

    setIsLoading(false);
  }, [assignmentId, getSupabase, user]);

  useEffect(() => { loadData(); }, [loadData]);

  async function handleCompleteAssignment() {
    if (!user || !assignment) return;
    setIsSubmitting(true);
    try {
      const supabase = await getSupabase();
      await supabase
        .from('assignments')
        .update({ status: 'tamamlandi', completed_at: new Date().toISOString() })
        .eq('id', assignment.id);
      setIsSubmitted(true);
      loadData();
    } catch (err) {
      console.error('Ödev teslim hatası:', err);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSaveDescription() {
    if (!assignment) return;
    setIsSavingDesc(true);
    try {
      const supabase = await getSupabase();
      await supabase
        .from('assignments')
        .update({ description: descText.trim() })
        .eq('id', assignment.id);

      setAssignment(prev => prev ? { ...prev, description: descText.trim() } : null);
      setIsEditingDesc(false);
    } catch (err) {
      console.error('Açıklama kaydetme hatası:', err);
    } finally {
      setIsSavingDesc(false);
    }
  }

  async function handleAddComment(e: React.FormEvent) {
    e.preventDefault();
    if (!newComment.trim() || !user || !assignment) return;
    const text = newComment.trim();
    setNewComment('');

    const optimisticComment: CommentItem = {
      id: Date.now().toString(),
      user_id: user.id,
      user_name: user.fullName || user.firstName || 'Kullanıcı',
      content: text,
      created_at: new Date().toISOString(),
    };

    setComments(prev => [...prev, optimisticComment]);

    try {
      const supabase = await getSupabase();
      const { error } = await supabase.from('class_comments').insert({
        class_id: assignment.class_id,
        assignment_id: assignment.id,
        author_id: user.id,
        author_name: user.fullName || user.firstName || 'Kullanıcı',
        author_role: user.id === assignment.teacher_id ? 'ogretmen' : 'ogrenci',
        content: text,
      });
      if (error) throw error;
    } catch (err) {
      console.error('Yorum ekleme hatası:', err);
      setComments(prev => prev.filter(c => c.id !== optimisticComment.id));
      showToast('Yorum gönderilemedi. Öğretmen yorumları kapatmış olabilir.', 'error');
    }
  }

  async function handleDeleteAssignment() {
    if (!assignment || !user) return;
    if (!window.confirm(`"${assignment.title}" ödevini sınıftan silmek (geri çekmek) istediğinize emin misiniz?`)) return;
    try {
      const supabase = await getSupabase();
      const { error } = await supabase
        .from('assignments')
        .delete()
        .eq('class_id', assignment.class_id)
        .eq('title', assignment.title);
      if (error) throw error;
      showToast('Ödev başarıyla geri çekildi/silindi.', 'success');
      navigate(`/sinif/${assignment.class_id}`);
    } catch (err) {
      console.error('Ödev silme hatası:', err);
      showToast('Ödev silinirken bir hata oluştu.', 'error');
    }
  }

  async function handleDeleteComment(commentId: string) {
    if (!window.confirm('Bu yorumu silmek istediğinize emin misiniz?')) return;
    try {
      const supabase = await getSupabase();
      const { error } = await supabase.from('class_comments').delete().eq('id', commentId);
      if (error) throw error;
      setComments(prev => prev.filter(c => c.id !== commentId));
      showToast('Yorum silindi.', 'success');
    } catch (err) {
      console.error('Yorum silme hatası:', err);
      showToast('Yorum silinirken hata oluştu.', 'error');
    }
  }

  if (isLoading) {
    return <div className="p-10 text-text-muted text-sm animate-pulse">Ödev detayları yükleniyor...</div>;
  }

  if (!assignment) {
    return <div className="p-10 text-text-muted text-sm">Ödev bulunamadı.</div>;
  }

  const targetUrl = getAssignmentUrl(assignment.type, assignment.resource_ref);

  return (
    <div className="p-4 md:p-6 lg:p-10 max-w-6xl mx-auto">
      <Helmet>
        <title>{assignment.title} — Ödev Detayı | Bitmiyo Sınıf</title>
        <meta name="description" content={`${assignment.title} ödev açıklaması ve teslim alanı.`} />
      </Helmet>

      {/* Geri Dönüş Linki */}
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-text-muted text-sm mb-4 hover:text-accent-600 transition-colors font-semibold"
      >
        <ArrowLeft size={14} /> Sınıf Akışına Dön
      </button>

      {/* Header Banner (Figma 3. Görsel Üst Kartı) */}
      <div className="relative overflow-hidden rounded-3xl bg-surface border border-border p-6 md:p-8 mb-6 shadow-sm">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
          <div className="flex items-center gap-2 text-xs font-bold text-accent-600">
            <Sparkles size={14} />
            <span>{classRow ? classRow.name : 'Sınıf Ödevi'}</span>
            <span className="text-text-muted">•</span>
            <span className="text-text-muted font-medium">{teacherName} Paylaşımı</span>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {user?.id === assignment.teacher_id && (
              <button
                type="button"
                onClick={handleDeleteAssignment}
                className="px-3 py-1 rounded-xl bg-danger-50 text-danger-600 hover:bg-danger-100 dark:bg-danger-950/40 dark:text-danger-400 font-bold text-xs flex items-center gap-1.5 transition-colors border border-danger-200 dark:border-danger-900"
                title="Ödevi Geri Çek / Sil"
              >
                <Trash2 size={13} />
                <span>Ödevi Geri Çek</span>
              </button>
            )}
            {assignment.due_date && (
              <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-100 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 font-bold text-xs">
                <Clock size={13} />
                <span>Son Teslim: {formatTurkishDueDate(assignment.due_date)}</span>
              </div>
            )}
          </div>
        </div>

        <h1 className="text-2xl md:text-4xl font-black text-text-primary mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
          {assignment.title}
        </h1>

        <Badge variant={assignment.type === 'test' ? 'accent' : assignment.type === 'video' ? 'accent' : 'neutral'}>
          {assignment.type === 'test' ? 'Test Ödevi' : assignment.type === 'video' ? 'Video' : 'Ödev'}
        </Badge>
      </div>

      {/* Figma 3. Görsel Grid: Sol Kolon (İçerik & Açıklama) + Sağ Kolon (Ödev Durumu & Yükleme Paneli) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

        {/* SOL KOLON (70%) */}
        <div className="lg:col-span-8 space-y-6">

          {/* Ödev Açıklaması Kartı & Canlı Düzenleme */}
          <Card className="p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-border pb-3">
              <h2 className="text-xs font-black uppercase tracking-widest text-text-secondary flex items-center gap-1.5">
                <FileText size={13} className="text-accent-500" /> Ödev Açıklaması & Yönergesi
              </h2>
              {user?.id === assignment.teacher_id && !isEditingDesc && (
                <button
                  onClick={() => setIsEditingDesc(true)}
                  className="text-xs font-bold text-accent-600 hover:underline flex items-center gap-1"
                >
                  <Pencil size={12} /> Yönergeyi Düzenle
                </button>
              )}
            </div>

            {isEditingDesc ? (
              <div className="space-y-3">
                <textarea
                  value={descText}
                  onChange={e => setDescText(e.target.value)}
                  placeholder="Öğrencilerin uygulayacağı adım adım ödev yönergesini yazın..."
                  rows={5}
                  className="w-full rounded-xl border border-border bg-surface p-3 text-sm outline-none focus:border-accent-500 font-medium"
                />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setIsEditingDesc(false)}>İptal</Button>
                  <Button
                    size="sm"
                    disabled={isSavingDesc}
                    onClick={handleSaveDescription}
                    className="bg-accent-600 text-white"
                  >
                    {isSavingDesc ? 'Kaydediliyor...' : 'Yönergeyi Kaydet'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-sm text-text-primary leading-relaxed space-y-3 font-medium">
                {assignment.description ? (
                  <p className="whitespace-pre-wrap">{assignment.description}</p>
                ) : (
                  <>
                    <p>
                      Lütfen aşağıda paylaşılan içerik ve problemleri dikkatle inceleyiniz. Çözümlerinizi veya çalışmanızı tamamladıktan sonra sağ taraftaki panel üzerinden dosyanızı yükleyebilir veya soru testini tamamlayabilirsiniz.
                    </p>
                    <p className="text-xs text-text-muted">
                      Çözümlerde her adımın açıkça belirtilmesi gerekmektedir. Kopya veya birebir aynı çözümler tespit edilirse çalışma değerlendirmeye alınmayacaktır.
                    </p>
                  </>
                )}
              </div>
            )}
          </Card>

          {/* Ekler / Kaynaklar Kartı */}
          {targetUrl && (
            <Card className="p-6 space-y-4">
              <h2 className="text-xs font-black uppercase tracking-widest text-text-secondary border-b border-border pb-3 flex items-center gap-1.5">
                <ExternalLink size={13} className="text-accent-500" /> Ekler & Kaynaklar
              </h2>

              <div className="p-4 rounded-2xl bg-purple-50 dark:bg-purple-500/10 border border-purple-200 dark:border-purple-500/20 flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  {assignment.type === 'test' ? <Target size={24} className="text-purple-600 shrink-0" /> : <Video size={24} className="text-purple-600 shrink-0" />}
                  <div>
                    <p className="font-bold text-sm text-text-primary">
                      {assignment.type === 'test' ? 'İnteraktif Test Ödevi (soru.bitmiyo.com.tr)' : 'Video Ders İçeriği'}
                    </p>
                    <p className="text-xs text-text-muted">Tek tıkla çözüme doğrudan başla</p>
                  </div>
                </div>

                <a
                  href={targetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-purple-600 hover:bg-purple-700 text-white font-bold text-xs px-4 py-2.5 rounded-xl flex items-center gap-1.5 shadow-sm transition-all"
                >
                  <ExternalLink size={14} /> {assignment.type === 'test' ? 'Testi Çöz' : 'İçeriğe Git'}
                </a>
              </div>
            </Card>
          )}

          {/* Sınıf Yorumları (Figma 3. Görsel Yorumlar Bölümü) */}
          <Card className="p-6 space-y-4">
            <h2 className="text-xs font-black uppercase tracking-widest text-text-secondary border-b border-border pb-3 flex items-center gap-1.5">
              <MessageCircle size={13} className="text-accent-500" /> Sınıf Yorumları <span>({comments.length})</span>
            </h2>

            {comments.length === 0 ? (
              <EmptyState icon={MessageCircle} title="Henüz yorum yok" description="İlk yorumu sen bırak — soru sormak veya geri bildirim vermek için aşağıyı kullan." />
            ) : (
              <div className="space-y-3">
                {comments.map((c, i) => (
                  <div
                    key={c.id}
                    className="flex gap-3 p-3 rounded-2xl bg-surface border border-border animate-fade-in-up"
                    style={{ animationDelay: `${i * 40}ms` }}
                  >
                    <Avatar name={c.user_name} size={32} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-xs text-text-primary">{c.user_name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] text-text-muted">
                            {new Date(c.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {(user?.id === c.user_id || user?.id === assignment.teacher_id) && (
                            <button
                              type="button"
                              onClick={() => handleDeleteComment(c.id)}
                              className="text-text-muted hover:text-danger-600 p-0.5 rounded transition-colors"
                              title="Sil"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      </div>
                      <p className="text-xs text-text-secondary leading-relaxed"><EmoteText text={c.content} /></p>
                      <ReactionBar contentType="class_comment" contentId={c.id} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Yorum Ekleme Formu */}
            {classRow?.students_can_comment === false && user?.id !== assignment.teacher_id ? (
              <div className="flex items-center gap-2.5 text-text-muted text-xs font-semibold pt-2">
                <Lock size={14} className="shrink-0" />
                Öğretmen bu sınıfta öğrenci yorumlarını kapattı.
              </div>
            ) : (
              <form onSubmit={handleAddComment} className="flex gap-2 pt-2">
                <input
                  ref={newCommentInputRef}
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  placeholder="Ödev hakkında bir soru sor veya yorum yaz..."
                  className="flex-1 rounded-xl border border-border bg-surface px-3 py-2 text-xs outline-none focus:border-accent-500 transition-colors"
                />
                <EmojiPickerButton textareaRef={newCommentInputRef} value={newComment} onChange={setNewComment} />
                <Button type="submit" size="sm" disabled={!newComment.trim()} className="bg-accent-600 text-white">
                  <Send size={14} /> Gönder
                </Button>
              </form>
            )}
          </Card>

        </div>

        {/* SAĞ KOLON (30% - Figma 3. Görsel Sağ Panel) */}
        <div className="lg:col-span-4 space-y-6">

          {/* Öğretmen Görünümü: Öğrenci Teslim Durumları Listesi */}
          {user?.id === assignment.teacher_id ? (
            <Card className="p-6 space-y-4 shadow-md">
              <div className="flex items-center justify-between border-b border-border pb-3">
                <h3 className="text-xs font-black uppercase tracking-widest text-text-secondary">
                  Öğrenci Teslim Durumları
                </h3>
                <Badge variant={studentStatuses.every(s => s.status === 'tamamlandi') && studentStatuses.length > 0 ? 'success' : 'accent'}>
                  {studentStatuses.filter(s => s.status === 'tamamlandi').length} / {studentStatuses.length} Teslim
                </Badge>
              </div>

              <div className="space-y-2 max-h-80 overflow-y-auto">
                {studentStatuses.length === 0 ? (
                  <p className="text-xs text-text-muted">Öğrenci verisi bulunamadı.</p>
                ) : (
                  studentStatuses.map(s => (
                    <div key={s.student_id} className="flex items-center justify-between p-2.5 rounded-xl bg-surface border border-border">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <Avatar name={s.display_name} size={28} />
                        <span className="text-xs font-bold text-text-primary truncate">{s.display_name}</span>
                      </div>
                      <Badge variant={s.status === 'tamamlandi' ? 'success' : 'warning'} className="shrink-0 text-[11px]">
                        {s.status === 'tamamlandi' ? 'Teslim Edildi' : 'Bekliyor'}
                      </Badge>
                    </div>
                  ))
                )}
              </div>
            </Card>
          ) : (
            /* Öğrenci Görünümü: Ödev Durumu & Yükleme Kartı */
            <Card className="p-6 space-y-4 shadow-md">
              <div className="flex items-center justify-between border-b border-border pb-3">
                <h3 className="text-xs font-black uppercase tracking-widest text-text-secondary">
                  Ödev Durumu
                </h3>
                <Badge variant={isSubmitted ? 'success' : 'warning'}>
                  {isSubmitted ? 'Teslim Edildi' : 'Teslim Edilmedi'}
                </Badge>
              </div>

              {!isSubmitted ? (
                <div className="space-y-4 animate-fade-in-up">
                  <p className="text-xs text-text-muted">
                    Bu çalışmayı tamamladığınızda aşağıdaki butona tıklayarak öğretmeninize teslim durumunuzu iletebilirsiniz.
                  </p>

                  <Button
                    onClick={handleCompleteAssignment}
                    disabled={isSubmitting}
                    className="w-full bg-accent-600 hover:bg-accent-700 text-white font-bold py-3 rounded-xl shadow-md transition-all flex items-center justify-center gap-2"
                  >
                    <CheckCircle2 size={16} />
                    {isSubmitting ? 'Gönderiliyor...' : 'Ödevi Teslim Et'}
                  </Button>
                </div>
              ) : (
                <div className="p-4 rounded-2xl bg-success-50 dark:bg-success-500/10 border border-success-200 dark:border-success-500/20 text-center space-y-2 animate-fade-in-up">
                  <PartyPopper size={32} className="text-success-500 mx-auto" />
                  <p className="font-bold text-sm text-success-700 dark:text-success-400">
                    Ödev Başarıyla Teslim Edildi!
                  </p>
                  <p className="text-xs text-text-muted">
                    Öğretmeniniz ödevinizi inceledikten sonra geri bildirim alacaksınız.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsSubmitted(false)}
                    className="mt-2 text-xs"
                  >
                    Teslimi Geri Al / Yeniden Yükle
                  </Button>
                </div>
              )}
            </Card>
          )}

          {/* Sınıf Öğretmen Bilgisi Kartı */}
          <Card className="p-5 flex items-center gap-3">
            <Avatar name={teacherName} size={40} />
            <div>
              <p className="font-bold text-sm text-text-primary">{teacherName}</p>
              <p className="text-xs text-text-muted">Ders Öğretmeni</p>
            </div>
          </Card>

        </div>

      </div>
    </div>
  );
}
